/**
 * Manufacturer Info Progress Display
 * Fetches cart data and displays progress for the manufacturer on PDP
 */

class ManufacturerInfoProgress {
  constructor(blockElement) {
    this.block = blockElement;
    this.manufacturerId = blockElement.dataset.manufacturerId;
    this.manufacturerType = parseInt(blockElement.dataset.manufacturerType) || 0;
    this.manufacturerCart = null;
    
    this.init();
  }

  async init() {
    // Wait for ManufacturerCart to be available
    if (typeof ManufacturerCart === 'undefined') {
      // Try again after a short delay
      setTimeout(() => this.init(), 100);
      return;
    }

    this.manufacturerCart = new ManufacturerCart();
    await this.updateProgress();
    
    // Listen for cart updates (cart:item-added is dispatched when adding from PDP/cards)
    document.addEventListener('cart:item-added', () => this.updateProgress());
    document.addEventListener('cart:updated', () => this.updateProgress());
    document.addEventListener('cart:change', () => this.updateProgress());
    document.addEventListener('line-item:change:end', () => this.updateProgress());
  }

  async updateProgress() {
    try {
      // Fetch current cart
      const cart = await this.manufacturerCart.fetchCart();
      if (!cart || !cart.items || cart.items.length === 0) {
        this.renderEmptyProgress();
        return;
      }

      // Group items by manufacturer
      const grouped = this.manufacturerCart.groupByManufacturer(cart.items);
      
      // Find items for this manufacturer
      let manufacturerItems = [];
      let manufacturer = null;
      
      for (const [id, group] of grouped.groups) {
        // Match by ID (either direct match or manufacturer.id match)
        const groupManufacturer = group.manufacturer;
        const groupManufacturerId = groupManufacturer?.id || '';
        const groupManufacturerName = groupManufacturer?.nombre?.value || groupManufacturer?.nombre || '';
        
        // Try to match by ID first
        if (id === this.manufacturerId || 
            groupManufacturerId === this.manufacturerId ||
            String(groupManufacturerId) === String(this.manufacturerId)) {
          manufacturerItems = group.items;
          manufacturer = groupManufacturer;
          break;
        }
        
        // Fallback: if our manufacturerId is actually a name-based ID, try matching by name
        if (this.manufacturerId && this.manufacturerId.startsWith('manufacturer_')) {
          const normalizedName = `manufacturer_${groupManufacturerName.replace(/\s+/g, '_').toLowerCase()}`;
          if (normalizedName === this.manufacturerId) {
            manufacturerItems = group.items;
            manufacturer = groupManufacturer;
            break;
          }
        }
      }

      if (manufacturerItems.length === 0) {
        this.renderEmptyProgress();
        return;
      }

      // Calculate progress based on manufacturer type
      let progressData = null;
      
      if (this.manufacturerType === 1) {
        progressData = await this.manufacturerCart.calculateType1Progress(manufacturer, manufacturerItems);
      } else if (this.manufacturerType === 2) {
        progressData = await this.manufacturerCart.calculateType2Progress(manufacturer, manufacturerItems);
      } else if (this.manufacturerType === 3) {
        progressData = await this.manufacturerCart.calculateType3Progress(manufacturer, manufacturerItems);
      } else if (this.manufacturerType === 4) {
        progressData = await this.manufacturerCart.calculateType4Progress(manufacturer, manufacturerItems);
      }

      if (progressData) {
        this.renderProgress(progressData);
      } else {
        this.renderEmptyProgress();
      }
    } catch (error) {
      console.error('Error updating manufacturer progress:', error);
      this.renderEmptyProgress();
    }
  }

  renderEmptyProgress() {
    const progressPercentage = this.block.querySelector('.progress-percentage');
    const progressBarFill = this.block.querySelector('.manufacturer-progress-bar-fill:not(.weight-bar)');
    const weightBarFill = this.block.querySelector('.manufacturer-progress-bar-fill.weight-bar');
    const weightCurrent = this.block.querySelector('.weight-current');
    const weightMax = this.block.querySelector('.weight-max');

    if (progressPercentage) {
      progressPercentage.textContent = '0.00%';
      progressPercentage.dataset.progress = '0';
    }
    
    if (progressBarFill) {
      progressBarFill.style.width = '0%';
    }
    
    if (weightBarFill) {
      weightBarFill.style.width = '0%';
    }
    
    if (weightCurrent) {
      weightCurrent.textContent = '0.0';
      weightCurrent.dataset.weight = '0';
    }
    
    if (weightMax) {
      const defaultMaxKg = parseInt(this.block.dataset.manufacturerMaxWeight, 10) || 35000;
      const maxWeightTons = (defaultMaxKg / 1000).toFixed(0);
      weightMax.textContent = `${maxWeightTons}T`;
      weightMax.dataset.weightMax = defaultMaxKg;
    }
  }

  renderProgress(progressData) {
    const progressPercentage = this.block.querySelector('.progress-percentage');
    const progressBarFill = this.block.querySelector('.manufacturer-progress-bar-fill:not(.weight-bar)');
    const weightBarFill = this.block.querySelector('.manufacturer-progress-bar-fill.weight-bar');
    const weightCurrent = this.block.querySelector('.weight-current');
    const weightMax = this.block.querySelector('.weight-max');

    if (progressData.type === 1) {
      // Type 1: Volume-based with weight
      // Use the first container's progress, or aggregate if multiple
      let percentage = 0;
      let totalWeight = 0;
      let maxWeight = 0;
      let weightPercent = 0;

      if (progressData.containers && progressData.containers.length > 0) {
        // Get the current (incomplete) container, or the last one if all complete
        const currentContainer = progressData.containers.find(c => !c.valid || c.percentage < 100) || 
                                 progressData.containers[progressData.containers.length - 1];
        
        percentage = currentContainer.percentage || 0;
        totalWeight = currentContainer.totalWeight || 0;
        maxWeight = progressData.maxWeight || 0;
        weightPercent = currentContainer.weightPercent || 0;
      } else {
        // Fallback to overall progress
        percentage = progressData.percentage || 0;
        totalWeight = progressData.totalWeight || 0;
        maxWeight = progressData.maxWeight || 0;
        weightPercent = progressData.weightPercent || 0;
      }

      if (progressPercentage) {
        progressPercentage.textContent = `${percentage.toFixed(2)}%`;
        progressPercentage.dataset.progress = percentage;
      }

      if (progressBarFill) {
        progressBarFill.style.width = `${Math.min(100, percentage)}%`;
      }

      if (weightBarFill) {
        weightBarFill.style.width = `${Math.min(100, weightPercent)}%`;
      }

      if (weightCurrent) {
        const weightTons = (totalWeight / 1000).toFixed(1);
        weightCurrent.textContent = weightTons;
        weightCurrent.dataset.weight = totalWeight;
      }

      if (weightMax) {
        const maxWeightTons = (maxWeight / 1000).toFixed(0);
        weightMax.textContent = `${maxWeightTons}T`;
        weightMax.dataset.weightMax = maxWeight;
      }
    } else if (progressData.type === 2) {
      // Type 2: Price-based
      const percentage = progressData.percentage || 0;
      
      if (progressPercentage) {
        progressPercentage.textContent = `${percentage.toFixed(2)}%`;
        progressPercentage.dataset.progress = percentage;
      }

      if (progressBarFill) {
        progressBarFill.style.width = `${Math.min(100, percentage)}%`;
      }

      // Hide weight bar for type 2
      if (weightBarFill) {
        weightBarFill.style.width = '0%';
      }

      if (weightCurrent) {
        weightCurrent.textContent = '0.0';
      }

      if (weightMax) {
        weightMax.textContent = '0T';
      }
    } else {
      // Type 3 or 4: Use overall percentage if available
      const percentage = progressData.percentage || 0;
      
      if (progressPercentage) {
        progressPercentage.textContent = `${percentage.toFixed(2)}%`;
        progressPercentage.dataset.progress = percentage;
      }

      if (progressBarFill) {
        progressBarFill.style.width = `${Math.min(100, percentage)}%`;
      }

      // Hide weight bar for other types
      if (weightBarFill) {
        weightBarFill.style.width = '0%';
      }

      if (weightCurrent) {
        weightCurrent.textContent = '0.0';
      }

      if (weightMax) {
        weightMax.textContent = '0T';
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const manufacturerInfoBlocks = document.querySelectorAll('.manufacturer-info-block[data-manufacturer-id]');
  manufacturerInfoBlocks.forEach(block => {
    new ManufacturerInfoProgress(block);
  });
});

// Also initialize if script loads after DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const manufacturerInfoBlocks = document.querySelectorAll('.manufacturer-info-block[data-manufacturer-id]');
    manufacturerInfoBlocks.forEach(block => {
      new ManufacturerInfoProgress(block);
    });
  });
} else {
  const manufacturerInfoBlocks = document.querySelectorAll('.manufacturer-info-block[data-manufacturer-id]');
  manufacturerInfoBlocks.forEach(block => {
    new ManufacturerInfoProgress(block);
  });
}

